import useAuth from '@State/Auth/useAuth';
import useImpersonate from '@State/Impersonate/useImpersonate';
import { useKeycloak } from '@State/Keycloak/KeycloakProvider';
import { Base, BaseFooter, BaseHeader, BaseLoader } from '@components/Base/index';
import PublicHeader from '@components/Header/PublicHeader';
import Loader from '@components/Loader';
import MaintenanceBanner from '@components/MaintenanceBanner';
import { PortalElement, PortalNamespace } from '@components/Portal';
import PublicFooter from '@components/PublicFooter';
import Sidebar from '@components/Sidebar';
import SidebarContext from '@components/Sidebar/SidebarContext';
import Snackbar from '@components/Snackbar';
import TermsAgreementModal from '@components/TermsAgreementModal';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ImpersonateBar from './ImpersonateBar';
import { useLoadingState } from './hooks/useLoadingState';
import Routes from './routes/index';
import ServiceUnavailable from './routes/ServiceUnavailable';
import { useRoutes } from './routes/useRoutes';

const AppLayout = styled.div<{ $impersonate?: boolean }>`
  display: flex;
  min-height: ${(props) =>
    props.$impersonate ? `calc(100vh - ${props.theme.size.impersonateBarHeight})` : '100vh'};

  ${(props) => props.theme.breakpoint.LtSm} {
    min-height: ${(props) =>
      props.$impersonate
        ? `calc(100vh - ${props.theme.size.impersonateBarHeightMobile})`
        : '100vh'};
  }
`;

const MainContent = styled.main<{ $impersonate?: boolean }>`
  margin-left: ${(props) => props.theme.size.sideBarWidth};
  flex: 1;
  min-height: ${(props) =>
    props.$impersonate ? `calc(100vh - ${props.theme.size.impersonateBarHeight})` : '100vh'};
  background-color: ${(props) => props.theme.color.bg.main};
  min-width: calc(
    100vw -
      ${(props) => props.theme.size.sideBarWidth} - max(
        env(safe-area-inset-left),
        ${(props) => props.theme.size.containerGutter}
      )
  );

  ${(props) => props.theme.breakpoint.LtSm} {
    min-width: 100vw;
    margin-left: 0;
    padding-top: ${(props) =>
      props.$impersonate
        ? `calc(3.5rem + ${props.theme.size.impersonateBarHeightMobile})`
        : props.theme.size.impersonateBarHeightMobile};
  }
`;

declare global {
  interface Window {
    _mtm: any;
  }
}

const RemoveTrailingSlash = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.endsWith('/') && location.pathname !== '/') {
      const newPathname = location.pathname.slice(0, -1);
      navigate(newPathname, { replace: true });
    }
  }, [location, navigate]);

  return null;
};

const PublicBase = () => {
  return (
    <>
      <BaseHeader>
        <PublicHeader />
      </BaseHeader>

      <Routes />

      <BaseFooter>
        <PublicFooter
          copyright={t('footer_copyright')}
          links={[
            {
              text: t('footer_link_privacy_policy_text'),
              title: t('footer_link_privacy_policy_title'),
              path: '/privacy-policy',
              routerLink: true,
            },
            {
              text: t('termsofuse_title'),
              title: t('termsofuse_title'),
              path: '/terms-of-use',
              routerLink: true,
            },
          ]}
        />
      </BaseFooter>
    </>
  );
};

interface AppContentProps {
  impersonate: boolean;
  maintenanceMode: boolean;
  shouldShowUserAgreement: boolean;
  isPrivateRoute: boolean;
  authenticated: boolean;
}

const AppContent: React.FC<AppContentProps> = ({
  impersonate,
  maintenanceMode,
  shouldShowUserAgreement,
  isPrivateRoute,
  authenticated,
}) => (
  <>
    <RemoveTrailingSlash />
    {shouldShowUserAgreement && <TermsAgreementModal />}
    <Snackbar />
    <Base>
      {!isPrivateRoute && !authenticated ? (
        <PublicBase />
      ) : (
        <>
          <ImpersonateBar />
          <SidebarContext>
            <AppLayout $impersonate={impersonate}>
              <Sidebar />
              <MainContent $impersonate={impersonate}>
                {maintenanceMode && <MaintenanceBanner />}
                <Routes />
              </MainContent>
            </AppLayout>
          </SidebarContext>
        </>
      )}
    </Base>
    <PortalElement id={PortalNamespace} />
  </>
);

const AppLoader: React.FC = () => {
  const isLoading = useLoadingState();
  const { parsedToken, authenticated } = useAuth();
  const { initialized, initError } = useKeycloak();
  const { isPrivateRoute } = useRoutes();
  const { impersonate } = useImpersonate();
  const shouldShowUserAgreement = Boolean(
    isPrivateRoute && parsedToken && parsedToken.terms?.v !== '1.0'
  );
  const maintenanceMode = false;

  // Keycloak-init gav upp (avvisades, timeout eller avvisad session). En
  // privat sida kan inte visas utan inloggning; en publik sida renderas som
  // oautentiserad via isLoading nedan, så att t.ex. QR-sidorna fungerar även
  // när Keycloak ligger nere.
  if (!initialized && initError && isPrivateRoute) {
    return (
      <Base>
        <ServiceUnavailable reason={initError} />
      </Base>
    );
  }

  if (isLoading) {
    return (
      <BaseLoader>
        <Loader />
      </BaseLoader>
    );
  }

  return (
    <AppContent
      impersonate={!!impersonate}
      maintenanceMode={maintenanceMode}
      shouldShowUserAgreement={shouldShowUserAgreement}
      isPrivateRoute={isPrivateRoute}
      authenticated={authenticated}
    />
  );
};

const App = () => {
  return <AppLoader />;
};

export default App;
